import { action } from '@ember/object';
import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismInput, PrismModal } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t, tKey } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import type { BudgetForecastEntry } from 'lumen/models';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';
import { localCopy } from 'tracked-toolbox';

export interface SettingsBudgetForecastModalsModalEditEntryTitleSignature {
  Args: {
    entry: BudgetForecastEntry;
    isTitleUnique: (title: string) => boolean;
    onClose: () => void;
  };
}

export default class SettingsBudgetForecastModalsModalEditEntryTitle extends Component<SettingsBudgetForecastModalsModalEditEntryTitleSignature> {
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];

  @localCopy('args.entry.titleWithDefault') declare title: string;

  editTitleTask = task(async (): Promise<void> => {
    const { entry } = this.args;
    const title = this.title.trim();

    if (title === entry.title) {
      this.args.onClose();
      return;
    }

    const isTitleValid = title.length > 0 && this.isTitleUnique;
    if (!isTitleValid) {
      return;
    }

    try {
      entry.title = this.title;
      await entry.save({
        adapterOptions: {
          usePatch: true,
        },
      });

      const translationKeyToUse =
        entry.dataType === 'budget'
          ? tKey('budget_forecast.budget.successfully_renamed')
          : tKey('budget_forecast.forecast.successfully_renamed');

      this.toast.success(
        this.intl.t(translationKeyToUse, { title: entry.title }),
        { showIcon: true },
      );

      this.args.onClose();
    } catch (error) {
      entry.rollbackAttributes();

      this.errorTracking.captureException(error, {
        feature: 'budget-forecast.edit-entry-title',
      });

      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  get isTitleUnique(): boolean {
    const { entry } = this.args;
    const title = this.title.trim();

    // Case insensitive as we don't want the selected entry to block itself
    const isTitleUnchanged = title.toLowerCase() === entry.title?.toLowerCase();
    if (isTitleUnchanged) {
      return true;
    }

    return this.args.isTitleUnique(this.title.trim());
  }

  @action
  onInputTitle(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.title = input.value;
  }

  <template>
    <PrismModal @onClose={{@onClose}} @title={{t "budget_forecast.edit_title"}}>
      <:default>
        <div class="modal--padded flex">
          <PrismInput
            @autofocus={{true}}
            @errorMessage={{t
              "budget_forecast.create_entry.error.title_not_unique"
            }}
            @hasErrors={{not this.isTitleUnique}}
            @onInput={{this.onInputTitle}}
            @placeholder={{t "budget_forecast.add_title"}}
            @removePadding={{true}}
            @required={{true}}
            @requiredErrorMessage={{t "general.required"}}
            @value={{this.title}}
            data-test-field="title"
          />
        </div>
      </:default>
      <:footer>
        <div class="flex h-full items-center justify-end gap-200 px-500">
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{this.editTitleTask.isRunning}}
            @label={{t "general.cancel"}}
            @onClick={{@onClose}}
            data-test-button="cancel"
          />
          <PrismButton
            @isDisabled={{this.editTitleTask.isRunning}}
            @isLoading={{this.editTitleTask.isRunning}}
            @label={{t "general.save"}}
            @onClick={{this.editTitleTask.perform}}
            data-test-button="save"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
